import { IAnyObject, isSuccess } from '@yimoka/shared';
import { IAPIExecutor, IStoreAPI, runAPI } from '@yimoka/store';
import { useCallback, useMemo } from 'react';

import { useAPIExecutor } from '../context/config';

interface UploadOptions {
  file: File;
  onSuccess?: (data: IAnyObject, fileOrXhr?: File | XMLHttpRequest) => void;
  onError?: (error: Error, file?: File) => void;
  onProgress?: (event: { percent: number }, file?: File) => void;
}

/**
 * 基于预签名 URL 执行对象存储直传，并把可访问 URL 回传给受控 Upload 组件。
 *
 * 业务文件不经过应用服务转存，既避免大文件占用 API 服务，也让调用方可直接把 URL 交给支持附件的大模型。
 */
export const useUploadS3 = (api: IStoreAPI, apiExecutor?: IAPIExecutor) => {
  const scopeAPIExecutor = useAPIExecutor();
  const curAPIExecutor = useMemo(() => apiExecutor ?? scopeAPIExecutor, [apiExecutor, scopeAPIExecutor]);

  return useCallback((opts: UploadOptions) => {
    const { file, onSuccess, onError, onProgress } = opts;

    // File 断言失败时不能继续走预签名流程，否则会把不完整的上传项永久留在 uploading 状态。
    if (!(file instanceof File)) {
      onError?.(new Error('无效的文件类型'), file);
      return;
    }

    // 先由业务 API 绑定上传目录和权限，再直传对象存储，避免前端自行拼接存储路径。
    runAPI(api, curAPIExecutor, { type: file.type, filename: file.name })
      .then((res) => {
        if (isSuccess(res)) {
          const { data } = res;
          const { uploadURL, cdnHost, accessURL } = data ?? {};

          // 没有预签名地址时立即结束本次 Upload，防止 UI 只显示一个无法恢复的等待项。
          if (!uploadURL) {
            onError?.(new Error('获取上传地址失败'), file);
            return;
          }

          const xhr = new XMLHttpRequest();

          // 100% 留给 load 事件确认；对象存储尚未应答时提前报满会让 UI 误以为附件可发送。
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
              const percent = Math.floor((event.loaded / event.total) * 10000) / 100;
              if (percent === 100) {
                onProgress({ percent: 99.99 }, file);
              } else {
                onProgress({ percent }, file);
              }
            }
          });

          xhr.addEventListener('load', () => {
            // S3 兼容对象存储可能以 200 或 204 表示 PUT 成功，统一按任意 2xx 完成受控文件状态迁移。
            if (xhr.status >= 200 && xhr.status < 300) {
              if (onProgress) {
                onProgress({ percent: 100 }, file);
              }
              if (accessURL) {
                onSuccess?.({ url: accessURL }, xhr);
                return;
              }
              // 构建最终的文件访问地址
              const url = xhr.responseURL;
              const urlObj = new URL(url);
              const { pathname, host, protocol } = urlObj;
              const fieldUrl = `${protocol}://${cdnHost ? cdnHost : host}${pathname}`;
              onSuccess?.({ url: fieldUrl }, xhr);
            } else {
              onError?.(new Error(`上传失败: HTTP ${xhr.status}`), file);
            }
          });

          // 网络层错误也必须回调给 Upload，才能让用户删除或重传，而不是被禁用发送区卡住。
          xhr.addEventListener('error', () => {
            onError?.(new Error('网络错误，上传失败'), file);
          });

          // 服务端签名覆盖 Content-Type；维持原始 MIME 让图片和 Office 文档都按原格式被模型识别。
          xhr.open('PUT', uploadURL);
          xhr.setRequestHeader('Content-Type', file.type);
          xhr.send(file);
        } else {
          onError?.(new Error('获取上传地址失败'), file);
        }
      })
      .catch((error) => {
        onError?.(error instanceof Error ? error : new Error('上传失败'), file);
      });
  }, [api, curAPIExecutor]);
};
